import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Snackbar,
  Stack,
  TextField
} from '@mui/material';
import axios from 'axios';
import { Form, Formik } from 'formik';
import React, { Fragment, useEffect, useState } from 'react';
import * as yup from 'yup';

const Horas = () => {
  const [dataCustomers, setDataCustomers] = useState([]);
  const [dataItems, setDataItems] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [openError, setOpenError] = React.useState(false);

  const validationSchema = yup.object().shape({
    fromHour: yup
      .string()
      .matches(/^([01]?\d|2[0-3]):[0-5]\d$/, 'Formato inválido (HH:mm)')
      .required('Ingrese la hora de inicio'),
    toHour: yup
      .string()
      .matches(/^([01]?\d|2[0-3]):[0-5]\d$/, 'Formato inválido (HH:mm)')
      .required('Ingrese la hora de fin')
      .test('is-greater', 'La hora de fin debe ser mayor que la de inicio', function (value) {
        const { fromHour } = this.parent;
        if (!fromHour || !value) return true;
        const [fromH, fromM] = fromHour.split(':').map(Number);
        const [toH, toM] = value.split(':').map(Number);
        return toH * 60 + toM > fromH * 60 + fromM;
      }),
    costo: yup.number().min(0, 'Elija un costo base mayor o igual a 0').required('Ingrese costo base'),
    dia: yup.date().required('Ingrese fecha'),
    observaciones: yup.string(),
    item: yup
      .object()
      .nullable()
      .test('valid-item', 'Elija un item', (value) => Boolean(value?.id)),
    customer: yup
      .object()
      .nullable()
      .test('valid-customer', 'Elija un cliente', (value) => Boolean(value?.id))
  });

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleCloseError = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenError(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: response } = await axios.request({ method: 'get', url: 'customers' });
        setDataCustomers(response);
      } catch (error) {
        console.error(error);
      }

      try {
        const { data: response } = await axios.get('items');
        setDataItems(response);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  return (
    <Fragment>
      <Card sx={{ maxWidth: 460 }}>
        <CardContent sx={{ p: 3 }}>
          <Formik
            initialValues={{
              item: null,
              customer: null,
              fromHour: '',
              toHour: '',
              costo: '',
              dia: '',
              observaciones: ''
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) => {
              const [fromH, fromM] = values.fromHour.split(':').map(Number);
              const [toH, toM] = values.toHour.split(':').map(Number);
              const horas = (toH * 60 + toM - (fromH * 60 + fromM)) / 60;

              const data = {
                customerId: values.customer.id,
                itemId: values.item.id,
                horas,
                costo: values.costo,
                fromHour: values.fromHour,
                toHour: values.toHour,
                dia: new Date(values.dia).toISOString(),
                observaciones: values.observaciones
              };

              try {
                await axios.post('horas', data);
                setOpen(true);
                resetForm();
              } catch (err) {
                console.log(err);
                setOpenError(true);
              }
            }}>
            {({ values, setFieldValue, touched, errors, handleChange }) => (
              <Form>
                <Stack spacing={2.5}>
                  <Autocomplete
                    id="customer"
                    fullWidth
                    value={values.customer}
                    getOptionLabel={(d) => d?.name || ''}
                    options={dataCustomers}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(e, value) => setFieldValue('customer', value || null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente"
                        variant="outlined"
                        error={touched.customer && Boolean(errors.customer)}
                        helperText={touched.customer && errors.customer}
                      />
                    )}
                  />
                  <Autocomplete
                    id="item"
                    fullWidth
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={values.item}
                    getOptionLabel={(d) => d?.name || ''}
                    options={dataItems}
                    onChange={(e, value) => {
                      setFieldValue('costo', value?.costoBase || '');
                      setFieldValue('item', value || null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item"
                        variant="outlined"
                        error={touched.item && Boolean(errors.item)}
                        helperText={touched.item && errors.item}
                      />
                    )}
                  />
                  <TextField
                    fullWidth
                    id="dia"
                    name="dia"
                    label="Día"
                    value={values.dia}
                    type="date"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    error={touched.dia && Boolean(errors.dia)}
                    helperText={touched.dia && errors.dia}
                  />
                  <TextField
                    fullWidth
                    id="fromHour"
                    name="fromHour"
                    label="Hora Inicio"
                    value={values.fromHour}
                    type="time"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    error={touched.fromHour && Boolean(errors.fromHour)}
                    helperText={touched.fromHour && errors.fromHour}
                  />
                  <TextField
                    fullWidth
                    id="toHour"
                    name="toHour"
                    label="Hora Fin"
                    value={values.toHour}
                    type="time"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    error={touched.toHour && Boolean(errors.toHour)}
                    helperText={touched.toHour && errors.toHour}
                  />
                  <TextField
                    fullWidth
                    id="costo"
                    name="costo"
                    label="Valor hora"
                    value={values.costo}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    error={touched.costo && Boolean(errors.costo)}
                    helperText={touched.costo && errors.costo}
                  />
                  <TextField
                    fullWidth
                    id="observaciones"
                    name="observaciones"
                    label="Observaciones"
                    value={values.observaciones}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    error={touched.observaciones && Boolean(errors.observaciones)}
                    helperText={touched.observaciones && errors.observaciones}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="submit" variant="contained" size="large" sx={{ minWidth: 140 }}>
                      Agregar
                    </Button>
                  </Box>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
          Horas agregadas!
        </Alert>
      </Snackbar>
      <Snackbar open={openError} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          Hubo un problema al agregar horas!
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

export default Horas;

